import { Alert, Button, Card, Group, Pagination, Select, Skeleton, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { IconAlertTriangle, IconDownload, IconReceiptOff, IconSearch } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { MyInvoiceApi } from "../../../apis";
import type { InvoiceSummary, PlatformInvoice, PlatformInvoiceStatus } from "../../../apis/platformInvoice";
import { EmptyState } from "../../../components/billing/EmptyState";
import { Message } from "../../../utils/message";
import { InvoiceKpis, type InvoiceKpiValues } from "./components/InvoiceKpis";
import { InvoiceTable } from "./components/InvoiceTable";
import { useChargeContract } from "./hooks/useChargeContract";
import { useMyInvoices } from "./hooks/useMyInvoices";

const PAGE_SIZE = 25;
const CURRENT_YEAR = new Date().getFullYear();

const statusOptions = [
  { value: "ISSUED", label: "Гаргасан" },
  { value: "SENT", label: "Илгээсэн" },
  { value: "PARTIALLY_PAID", label: "Хэсэгчлэн төлсөн" },
  { value: "PAID", label: "Төлсөн" },
  { value: "OVERDUE", label: "Хугацаа хэтэрсэн" },
  { value: "VOID", label: "Хүчингүй" }
];

const yearOptions = Array.from({ length: 5 }, (_, i) => {
  const y = CURRENT_YEAR - i;
  return { value: String(y), label: `${y} он` };
});

interface Props {
  /** Reports the loaded invoice count up to the parent for the tab badge. */
  onCountChange?: (count: number) => void;
}

export function InvoicesTab({ onCountChange }: Props) {
  const [status, setStatus] = useState<PlatformInvoiceStatus | "">("");
  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  // Search is now applied server-side (backend supports `year` + `search`).
  const { rows, summary, error, isLoading, reload } = useMyInvoices({ year, status, search });
  // Read-only contract lookup powers the client-derived "next invoice" fallback.
  const { contract } = useChargeContract();

  useEffect(() => {
    onCountChange?.(summary?.total ?? rows.length);
  }, [summary?.total, rows.length, onCountChange]);

  const currency = rows[0]?.currency ?? contract?.currency ?? "MNT";

  const nextEstimate = useMemo(() => {
    return (contract?.components ?? [])
      .filter((c) => c.componentType === "RECURRING_SUBSCRIPTION" && c.isEnabled !== false)
      .reduce((s, c) => s + Number(c.recurringAmount ?? 0), 0);
  }, [contract]);

  // Prefer the server summary block; fall back to client-derived figures when
  // the deployed backend does not return one.
  const kpis: InvoiceKpiValues = useMemo(
    () => (summary ? summaryToKpis(summary) : computeKpis(rows, nextEstimate, contract?.components)),
    [summary, rows, nextEstimate, contract]
  );

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleExport = async () => {
    try {
      setExporting(true);
      await MyInvoiceApi.exportInvoicesCsv({
        status: status || undefined,
        year,
        search: search.trim() || undefined
      });
    } catch {
      Message.error("CSV татахад алдаа гарлаа");
    } finally {
      setExporting(false);
    }
  };

  const handleOpenPdf = async (invoice: PlatformInvoice) => {
    try {
      await MyInvoiceApi.openInvoicePdf(invoice.id);
    } catch {
      Message.error("PDF нээхэд алдаа гарлаа");
    }
  };

  return (
    <Stack gap="lg">
      <InvoiceKpis values={kpis} currency={currency} year={year} loading={isLoading} />

      <Card withBorder radius="md" padding="md">
        <Stack gap="md">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <Group gap="sm" wrap="wrap">
              <Select
                placeholder="Бүх төлөв"
                data={statusOptions}
                value={status || null}
                onChange={(v) => {
                  setStatus((v as PlatformInvoiceStatus) || "");
                  setPage(1);
                }}
                clearable
                w={200}
              />
              <Select
                data={yearOptions}
                value={String(year)}
                onChange={(v) => {
                  setYear(Number(v) || CURRENT_YEAR);
                  setPage(1);
                }}
                w={140}
              />
              <TextInput
                placeholder="Нэхэмжлэх №-ээр хайх"
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  setPage(1);
                }}
                w={240}
              />
            </Group>
            <Tooltip label="CSV татах" withArrow>
              <Button
                variant="default"
                leftSection={<IconDownload size={18} />}
                loading={exporting}
                disabled={rows.length === 0}
                onClick={handleExport}
              >
                CSV татах
              </Button>
            </Tooltip>
          </Group>

          {error ? (
            <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Нэхэмжлэх ачааллахад алдаа гарлаа">
              <Button mt="sm" size="xs" variant="light" color="red" onClick={reload}>
                Дахин ачаалах
              </Button>
            </Alert>
          ) : isLoading ? (
            <Skeleton height={240} radius="md" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<IconReceiptOff size={26} />}
              message={
                search || status
                  ? "Шүүлтүүрт тохирох нэхэмжлэх олдсонгүй."
                  : "Одоогоор нэхэмжлэх алга. Эхний нэхэмжлэх гэрээний нөхцөлийн дагуу үүснэ."
              }
            />
          ) : (
            <>
              <InvoiceTable rows={pageRows} onOpenPdf={handleOpenPdf} />
              <Group justify="space-between" align="center" wrap="wrap" gap="sm">
                <Text size="sm" c="dimmed">
                  {rows.length} нэхэмжлэх · {(safePage - 1) * PAGE_SIZE + 1}-
                  {Math.min(safePage * PAGE_SIZE, rows.length)} харуулж байна
                </Text>
                {pageCount > 1 && (
                  <Pagination total={pageCount} value={safePage} onChange={setPage} size="sm" />
                )}
              </Group>
            </>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

// Map the server summary block straight onto the KPI tiles (source of truth).
function summaryToKpis(summary: InvoiceSummary): InvoiceKpiValues {
  return {
    total: summary.total,
    nextDate: summary.nextInvoice?.date ?? null,
    nextAmount: summary.nextInvoice?.amount ?? 0,
    outstanding: summary.outstanding,
    paidThisYear: summary.paidThisYear,
    paidCount: summary.paidCount
  };
}

// Fallback: derive KPI figures client-side when the backend returns no summary.
function computeKpis(
  rows: PlatformInvoice[],
  nextEstimate: number,
  components?: { componentType: string; recurringAnchorDay?: number | null }[]
): InvoiceKpiValues {
  const num = (v: string | null | undefined) => Number(v ?? 0) || 0;
  const outstanding = rows
    .filter((r) => r.status === "ISSUED" || r.status === "SENT" || r.status === "PARTIALLY_PAID" || r.status === "OVERDUE")
    .reduce((s, r) => s + num(r.amountDue), 0);
  const paid = rows.filter((r) => r.status === "PAID");
  const paidThisYear = paid.reduce((s, r) => s + num(r.totalAmount), 0);

  // Next anchor date derived from the recurring component's anchor day.
  const anchorDay =
    components?.find((c) => c.componentType === "RECURRING_SUBSCRIPTION")?.recurringAnchorDay ?? 1;
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth(), anchorDay);
  if (next <= now) next.setMonth(next.getMonth() + 1);

  return {
    total: rows.length,
    nextDate: nextEstimate > 0 ? next.toISOString() : null,
    nextAmount: nextEstimate,
    outstanding,
    paidThisYear,
    paidCount: paid.length
  };
}
